import {ActivityIndicator, Keyboard, StyleSheet} from 'react-native';
import React, {useCallback, useEffect, useMemo} from 'react';
import {
  RecommendationsForSubParams,
  RecommendedItemCell,
} from '../../../../../src/itemDetails/types/itemReplacement';
import {getClientType, getStoreName} from '@buncha/common/types/common';

import {Analytics} from '@buncha/config/analytics/analytics';
import {Box} from '../../../../../src/components/core/view';
import {CustomFlatList} from '../../../../../src/components/core/list';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import {H5Text} from '../../../../../src/components/core/text';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import NewFeatureBanner from '@buncha/common/components/NewFeatureBanner/NewFeatureBanner';
import ReplacementItemCell from '../replacementItemCell';
import {ShopperScreens} from '@buncha/home/navigation/types';
import Touchable from '@buncha/components/core/touchable/Touchable';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetRecommendedItemsForSubstitution} from '../../../../../src/itemDetails/hooks/itemReplacement';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useGetLocale} from '@buncha/localization/hooks';

interface RecommendedItemsProps {
  catalogItemId: number;
  category: string;
  searchQuery: string;
  storeId: number;
  subCategory: string;
  isUPCMode: boolean;
  orderItemId?: number;
  orderId?: number;
  runId?: number;
  quantity?: number;
}

interface BannerData {
  visible: boolean;
  title: string;
}

const RecommendedItems: React.FC<RecommendedItemsProps> = props => {
  const catalogItemId = props?.catalogItemId || 0;
  const category = props?.category || '';
  const searchQuery = props?.searchQuery || '';
  const storeId = props?.storeId || 0;
  const subCategory = props?.subCategory || '';
  const {upcOrderItem} = useAppSelector(gState => gState.itemScanner);
  const constants = useAppSelector(gState => gState.onboarding.constants);
  const isB2BStore = useIsMeijerStore(storeId);
  const {title} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const parsedBannerData: BannerData = useMemo(() => {
    let parsedData = {visible: false, title: ''};

    if (constants?.SHOPPER_ITEM_DESCRIPTION_BANNER)
      try {
        parsedData = JSON.parse(constants?.SHOPPER_ITEM_DESCRIPTION_BANNER);
      } catch (error) {
        parsedData = {visible: false, title: ''};
      }

    return parsedData;
  }, [constants?.SHOPPER_ITEM_DESCRIPTION_BANNER]);

  const {
    getRecommendedItemsForSubstitution,
    loading,
    data,
    getRecommendedItemsForSubstitutionInUPC,
  } = useGetRecommendedItemsForSubstitution();

  const renderItem = useCallback(
    (subItem: {item: RecommendedItemCell}) => {
      return (
        <ReplacementItemCell
          orderItemId={props?.orderItemId}
          orderId={props?.orderId}
          runId={props?.runId}
          quantity={props?.quantity}
          itemInfo={subItem?.item}
          isUPCMode={props?.isUPCMode}
          isB2BStore={isB2BStore}
        />
      );
    },
    [
      isB2BStore,
      props?.isUPCMode,
      props?.orderId,
      props?.orderItemId,
      props?.quantity,
      props?.runId,
    ],
  );
  const paramsToSend: RecommendationsForSubParams = {
    catalogItemId: catalogItemId,
    searchQuery: searchQuery,
    category: category,
    storeId: storeId,
    subCategory: subCategory,
  };
  useEffect(() => {
    if (props?.isUPCMode)
      getRecommendedItemsForSubstitutionInUPC(upcOrderItem?.id ?? 1);
    else if (paramsToSend?.storeId)
      getRecommendedItemsForSubstitution(paramsToSend);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsToSend?.storeId]);

  useEffect(() => {
    if (data.length)
      Analytics.eventWithProps('Substitution Page', {
        type: EventType.page,
        source: 'Add Replacement Page',
        substitutionChoices: data,
        recommendedDataCount: data.length,
        client: getClientType(isB2BStore),
        runId: props?.runId,
        storeType: getStoreName(String(storeId)),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const separator = useCallback(() => <Divider />, []);

  const handleScroll = useCallback(() => {
    Keyboard.dismiss();
  }, []);

  if (loading) return <ActivityIndicator />;
  return (
    <Box style={styles.parentBox}>
      <H5Text style={styles.text} alignText="left">
        {title?.recommendedItems}
      </H5Text>
      <If condition={parsedBannerData?.visible}>
        <Touchable onPress={handleScroll}>
          <NewFeatureBanner title={parsedBannerData?.title} />
        </Touchable>
      </If>
      <CustomFlatList
        scrollEnabled={true}
        data={data}
        showsVerticalScrollIndicator={false}
        renderItem={renderItem}
        ItemSeparatorComponent={separator}
        onScroll={handleScroll}
      />
    </Box>
  );
};

export default React.memo(RecommendedItems);

const styles = StyleSheet.create({
  parentBox: {
    flex: 1,
    marginTop: 6,
  },
  text: {
    paddingBottom: 5,
  },
});
